/**
 * AgentOps Widget Definitions
 *
 * Defines all widgets for the AgentOps dashboard using the template system.
 * Each widget specifies its data transformation from the source data.
 */

import {
  createMetricWidget,
  createChartWidget,
  createListWidget,
  WIDGET_CATEGORIES,
  WIDGET_SIZES,
  WIDGET_ICONS,
  DATA_SOURCES,
  WidgetTemplate,
} from '../widgetTemplates';
import { GREEN, RED, AMBER, BLUE } from '../../../constants/chartColors';

/** Agent run record from the AgentOps API */
export interface AgentRun {
  status: string;
  agent_id?: string;
  agent_name?: string;
  agent_type?: string;
  created_at?: string;
  start_time: string;
  duration_ms?: number;
  steps?: number;
  step_count?: number;
  retries?: number;
  cost?: number;
  decisions?: number;
  human_interventions?: number;
}

/** Agent tool record from the AgentOps API */
export interface AgentTool {
  tool_name?: string;
  name?: string;
  call_count?: number;
  usage_count?: number;
  success_rate?: number;
  error_count?: number;
}

/** Agent incident record from the AgentOps API */
export interface AgentIncident {
  title?: string;
  description?: string;
  severity?: string;
  status?: string;
  created_at?: string;
  timestamp?: string;
  resolved_at?: string;
}

const {
  FiActivity,
  FiPlay,
  FiCheckCircle,
  FiAlertTriangle,
  FiShield,
  FiDatabase,
  FiCpu,
  FiTrendingUp,
  FiBarChart2,
  FiUsers,
  FiBell,
  FiBox,
} = WIDGET_ICONS;

/**
 * Total Agent Runs Widget
 * Shows the total number of agent executions
 */
export const agentRunsCountWidget = createMetricWidget({
  id: 'agent-runs-count',
  name: 'Total Agent Runs',
  description: 'All agent executions',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.totalRuns || 0,
    label: 'Total Agent Runs',
    format: 'number',
    color: 'cyan',
    trend: 'up',
    trendValue: `${sourceData?.completedRuns || 0} completed`,
    link: '/ai-operations',
  }),
});

/**
 * Active Runs Widget
 * Shows currently running agents
 */
export const activeRunsWidget = createMetricWidget({
  id: 'agentops-active-runs',
  name: 'Active Runs',
  description: 'Currently running agents',
  icon: FiPlay,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 30000,

  getData: (sourceData) => ({
    value: sourceData?.activeRuns || 0,
    label: 'Active Runs',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Success Rate Widget
 * Shows the percentage of successful agent runs
 */
export const successRateWidget = createMetricWidget({
  id: 'agent-success-rate',
  name: 'Success Rate',
  description: 'Successful run percentage',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const successRate = sourceData?.successRate || 0;

    // Determine color based on success rate
    let color = 'red';
    if (successRate >= 90) color = 'green';
    else if (successRate >= 70) color = 'amber';

    return {
      value: successRate,
      label: 'Success Rate',
      format: 'percentage',
      color,
      link: '/ai-operations',
    };
  },
});

/**
 * Total Tools Widget
 * Shows the number of unique tools available
 */
export const totalToolsWidget = createMetricWidget({
  id: 'agentops-total-tools',
  name: 'Total Tools',
  description: 'Unique tools available',
  icon: FiBox,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.totalTools || 0,
    label: 'Total Tools',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Safety Violations Widget
 * Shows the number of policy violations detected
 */
export const safetyViolationsWidget = createMetricWidget({
  id: 'agentops-safety-violations',
  name: 'Safety Violations',
  description: 'Policy violations detected',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const violations = sourceData?.safetyViolations || 0;

    return {
      value: violations,
      label: 'Safety Violations',
      format: 'number',
      color: violations > 0 ? 'red' : 'green',
      link: '/ai-operations',
    };
  },
});

/**
 * Safety Score Widget
 * Shows overall safety compliance rate
 */
export const safetyScoreWidget = createMetricWidget({
  id: 'safety-score',
  name: 'Safety Score',
  description: 'Overall safety compliance',
  icon: FiShield,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const complianceRate = sourceData?.complianceRate || 0;

    // Determine color based on compliance rate
    let color = 'red';
    if (complianceRate >= 95) color = 'green';
    else if (complianceRate >= 80) color = 'amber';

    return {
      value: complianceRate,
      label: 'Safety Score',
      format: 'percentage',
      color,
      link: '/ai-operations',
    };
  },
});

/**
 * Health Score Widget
 * Shows APM health indicator
 */
export const healthScoreWidget = createMetricWidget({
  id: 'agentops-health-score',
  name: 'Health Score',
  description: 'APM health indicator',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => {
    const healthScore = sourceData?.healthScore || 0;

    // Determine color based on health score
    let color = 'red';
    if (healthScore >= 80) color = 'green';
    else if (healthScore >= 60) color = 'amber';

    return {
      value: healthScore,
      label: 'Health Score',
      format: 'percentage',
      color,
      link: '/ai-operations',
    };
  },
});

/**
 * Heap Usage Widget
 * Shows memory heap utilization
 */
export const heapUsageWidget = createMetricWidget({
  id: 'agentops-heap-usage',
  name: 'Heap Usage',
  description: 'Memory heap utilization',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => {
    const heapUsage = sourceData?.heapUsage || 0;

    // Determine color based on heap usage (inverted - high is bad)
    let color = 'green';
    if (heapUsage > 80) color = 'red';
    else if (heapUsage > 60) color = 'amber';

    return {
      value: heapUsage,
      label: 'Heap Usage',
      format: 'percentage',
      color,
      link: '/ai-operations',
    };
  },
});

/**
 * CPU Usage Widget
 * Shows CPU utilization percentage
 */
export const cpuUsageWidget = createMetricWidget({
  id: 'agentops-cpu-usage',
  name: 'CPU Usage',
  description: 'CPU utilization percentage',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => {
    const cpuUsage = sourceData?.cpuUsage || 0;

    // Determine color based on CPU usage (inverted - high is bad)
    let color = 'green';
    if (cpuUsage > 80) color = 'red';
    else if (cpuUsage > 60) color = 'amber';

    return {
      value: cpuUsage,
      label: 'CPU Usage',
      format: 'percentage',
      color,
      link: '/ai-operations',
    };
  },
});

/**
 * Tool Usage Chart Widget
 * Shows the most used agent tools as a bar chart
 */
export const toolUsageWidget = createChartWidget({
  id: 'tool-usage',
  name: 'Tool Usage',
  description: 'Most used agent tools',
  icon: FiBox,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const tools = sourceData?.tools || [];
    const data = tools.slice(0, 5).map((t: AgentTool) => ({
      name: t.tool_name || t.name || 'Unknown',
      value: t.call_count || t.usage_count || 0,
    }));

    return {
      chartType: 'bar',
      data: data.length > 0 ? data : [{ name: 'No tools', value: 0 }],
      xKey: 'name',
      yKey: 'value',
      color: '#06B6D4',
      link: '/agentops/tools',
    };
  },
});

/**
 * Runs Timeline Chart Widget
 * Shows agent runs over time as a bar chart
 * Respects the global time filter to display appropriate date ranges
 */
export const runsTimelineWidget = createChartWidget({
  id: 'agentops-runs-timeline',
  name: 'Runs Over Time',
  description: 'Agent runs timeline',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData, context) => {
    const runs = sourceData?.runs || [];
    const dateRange = context?.dateRange;

    // Determine the date range to display based on context
    const now = new Date();
    let startDate, endDate, granularity;

    if (dateRange?.startDate && dateRange?.endDate) {
      startDate = new Date(dateRange.startDate);
      endDate = new Date(dateRange.endDate);
    } else if (dateRange?.preset) {
      endDate = now;
      switch (dateRange.preset) {
        case 'today':
          startDate = new Date(now);
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'last_7_days':
          startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
          break;
        case 'last_30_days':
          startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
          break;
        case 'last_90_days':
          startDate = new Date(now.getTime() - 89 * 24 * 60 * 60 * 1000);
          break;
        case 'this_year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case 'last_year':
          startDate = new Date(now.getFullYear() - 1, 0, 1);
          endDate = new Date(now.getFullYear() - 1, 11, 31);
          break;
        case 'all_time': {
          // For all time, find the oldest run or default to 1 year
          const oldestRun = runs.length > 0
            ? new Date(Math.min(...runs.map((r: AgentRun) => new Date(r.created_at || r.start_time).getTime())))
            : new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          startDate = oldestRun;
          break;
        }
        default:
          // Default to last 7 days
          startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      }
    } else {
      // Default to last 7 days if no context
      startDate = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      endDate = now;
    }

    // Calculate the number of days in the range
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));

    // Determine granularity based on range
    if (daysDiff > 365) {
      granularity = 'month';
    } else if (daysDiff > 90) {
      granularity = 'week';
    } else {
      granularity = 'day';
    }

    // Generate timeline buckets based on granularity
    const grouped: Record<string, { date: string; count: number; bucketStart: Date }> = {};
    let current = new Date(startDate);
    current.setHours(0, 0, 0, 0);

    while (current <= endDate) {
      let key, bucketStart;

      if (granularity === 'month') {
        key = current.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        bucketStart = new Date(current.getFullYear(), current.getMonth(), 1);
        const next = new Date(current);
        next.setMonth(next.getMonth() + 1);
        current = next;
      } else if (granularity === 'week') {
        // Start of week (Sunday)
        const weekStart = new Date(current);
        weekStart.setDate(current.getDate() - current.getDay());
        key = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        bucketStart = weekStart;
        const next = new Date(current);
        next.setDate(next.getDate() + 7);
        current = next;
      } else {
        key = current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        bucketStart = new Date(current);
        const next = new Date(current);
        next.setDate(next.getDate() + 1);
        current = next;
      }

      if (!grouped[key]) {
        grouped[key] = { date: key, count: 0, bucketStart };
      }
    }

    // Count runs per bucket
    runs.forEach((run: AgentRun) => {
      const runDate = new Date(run.created_at || run.start_time);
      if (runDate < startDate || runDate > endDate) return;

      let key;
      if (granularity === 'month') {
        key = runDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else if (granularity === 'week') {
        const weekStart = new Date(runDate);
        weekStart.setDate(runDate.getDate() - runDate.getDay());
        key = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        key = runDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }

      if (grouped[key]) {
        grouped[key].count++;
      }
    });

    // Sort by date and extract data
    const data = Object.values(grouped)
      .sort((a, b) => (a.bucketStart?.getTime() || 0) - (b.bucketStart?.getTime() || 0))
      .map(({ date, count }) => ({ date, count }));

    return {
      chartType: 'bar',
      data: data.length > 0 ? data : [{ date: 'No data', count: 0 }],
      xKey: 'date',
      yKey: 'count',
      color: BLUE,
      link: '/agentops/runs',
    };
  },
});

/**
 * Tool Success Rate Chart Widget
 * Shows success rate by tool as a horizontal bar chart
 */
export const toolSuccessWidget = createChartWidget({
  id: 'agentops-tool-success',
  name: 'Tool Success Rate',
  description: 'Success rate by tool',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const tools = sourceData?.tools || [];
    const data = tools.slice(0, 10).map((t: AgentTool) => ({
      name: t.tool_name || t.name || 'Unknown',
      value: t.success_rate || 100,
    }));

    return {
      chartType: 'horizontalBar',
      data: data.length > 0 ? data : [{ name: 'No tools', value: 0 }],
      xKey: 'name',
      yKey: 'value',
      color: GREEN,
      format: 'percentage',  // Display as percentage, not currency
      link: '/agentops/tools',
    };
  },
});

/**
 * Recent Incidents List Widget
 * Shows the latest safety incidents
 */
export const incidentsWidget = createListWidget({
  id: 'agentops-incidents',
  name: 'Recent Incidents',
  description: 'Latest safety incidents',
  icon: FiBell,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const incidents = sourceData?.incidents || [];

    const items = incidents.slice(0, 5).map((inc: AgentIncident) => ({
      icon: '\u26A0\uFE0F',
      title: inc.title || inc.description || 'Incident',
      subtitle: inc.severity || 'Info',
      timestamp: new Date(inc.created_at || inc.timestamp || '').toLocaleDateString(),
      badge: {
        text: inc.status || 'Open',
        color: inc.status === 'resolved' ? 'green' : 'red',
      },
    }));

    return {
      items,
      maxItems: 5,
      showBadge: true,
    };
  },
});

/**
 * Top Agents List Widget
 * Shows the most active agents ranked by total run count.
 * Success rate is displayed as a percentage of completed runs.
 * "100% Success" badge indicates all runs completed successfully.
 */
export const topAgentsWidget = createListWidget({
  id: 'agentops-top-agents',
  name: 'Top Agents',
  description: 'Most active agents ranked by run count',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const runs = sourceData?.runs || [];

    // Group runs by agent
    interface AgentStat { count: number; success: number }
    const agentStats = runs.reduce((acc: Record<string, AgentStat>, run: AgentRun) => {
      const agentId = run.agent_id || run.agent_name || 'Unknown';
      if (!acc[agentId]) {
        acc[agentId] = { count: 0, success: 0 };
      }
      acc[agentId].count++;
      if (run.status === 'completed') acc[agentId].success++;
      return acc;
    }, {} as Record<string, AgentStat>);

    // Sort by count (ranking criteria: most active agents) and take top 5
    const topAgents = (Object.entries(agentStats) as [string, AgentStat][])
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([name, stats]) => {
        const successRate = ((stats.success / stats.count) * 100).toFixed(0);
        const isPerfect = stats.success === stats.count;
        return {
          icon: '\uD83E\uDD16',
          title: name,
          subtitle: `${stats.count} runs`,
          value: `${successRate}%`,
          tooltip: isPerfect
            ? 'All runs completed successfully'
            : `${stats.success} of ${stats.count} runs completed`,
          badge: {
            text: isPerfect ? '100% Success' : 'Active',
            color: isPerfect ? 'green' : 'blue',
            tooltip: isPerfect
              ? 'All runs completed successfully - no failures'
              : `Success rate: ${successRate}%`,
          },
        };
      });

    return {
      items: topAgents,
      maxItems: 5,
      showBadge: true,
    };
  },
});

// ============================================
// ADDITIONAL AGENTOPS WIDGETS (Phase 10)
// ============================================

/**
 * Avg Run Duration Widget
 */
export const avgRunDurationWidget = createMetricWidget({
  id: 'avg-run-duration-agent',
  name: 'Avg Agent Duration',
  description: 'Average execution time',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const avgDuration = runs.length > 0
      ? runs.reduce((sum: number, r: AgentRun) => sum + (r.duration_ms || 0), 0) / runs.length
      : 0;
    return { value: Math.round(avgDuration), label: 'Avg Duration', format: 'duration', color: 'cyan', link: '/ai-operations' };
  },
});

/**
 * Tool Calls Count Widget
 */
export const toolCallsCountWidget = createMetricWidget({
  id: 'tool-calls-count',
  name: 'Tool Calls',
  description: 'Total tool invocations',
  icon: FiBox,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const tools = sourceData?.tools || [];
    const totalCalls = tools.reduce((sum: number, t: AgentTool) => sum + (t.call_count || 0), 0);
    return { value: totalCalls, label: 'Tool Calls', format: 'number', color: 'blue', link: '/ai-operations' };
  },
});

/**
 * Active Incidents Widget
 */
export const activeIncidentsWidget = createMetricWidget({
  id: 'active-incidents',
  name: 'Active Incidents',
  description: 'Open incident count',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const incidents = sourceData?.incidents?.filter((i: AgentIncident) => i.status !== 'resolved') || [];
    return { value: incidents.length, label: 'Active Incidents', format: 'number', color: incidents.length > 0 ? 'red' : 'green', link: '/ai-operations' };
  },
});

/**
 * Agent Types Widget
 */
export const agentTypesWidget = createMetricWidget({
  id: 'agent-types',
  name: 'Agent Types',
  description: 'Unique agent types',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const uniqueTypes = new Set(runs.map((r: AgentRun) => r.agent_type || r.agent_name)).size;
    return { value: uniqueTypes, label: 'Agent Types', format: 'number', color: 'purple', link: '/ai-operations' };
  },
});

/**
 * Total Steps Widget
 */
export const totalStepsWidget = createMetricWidget({
  id: 'total-steps',
  name: 'Total Steps',
  description: 'Agent steps executed',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const totalSteps = runs.reduce((sum: number, r: AgentRun) => sum + (r.steps || r.step_count || 0), 0);
    return { value: totalSteps, label: 'Total Steps', format: 'number', color: 'blue', link: '/ai-operations' };
  },
});

/**
 * Avg Steps Per Run Widget
 */
export const avgStepsPerRunWidget = createMetricWidget({
  id: 'avg-steps-per-run',
  name: 'Avg Steps/Run',
  description: 'Average steps per run',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const totalSteps = runs.reduce((sum: number, r: AgentRun) => sum + (r.steps || r.step_count || 0), 0);
    const avg = runs.length > 0 ? totalSteps / runs.length : 0;
    return { value: Math.round(avg * 10) / 10, label: 'Steps/Run', format: 'number', color: 'cyan', link: '/ai-operations' };
  },
});

/**
 * Memory Store Size Widget
 */
export const memoryStoreSizeWidget = createMetricWidget({
  id: 'memory-store-size',
  name: 'Memory Store',
  description: 'Total memory entries',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.apmMetrics?.memory_entries || 0,
    label: 'Memory Entries',
    format: 'number',
    color: 'purple',
    link: '/ai-operations',
  }),
});

/**
 * Guardrail Triggers Widget
 */
export const guardrailTriggersWidget = createMetricWidget({
  id: 'guardrail-triggers',
  name: 'Guardrail Triggers',
  description: 'Safety checks triggered',
  icon: FiShield,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const triggers = sourceData?.safetyViolations || 0;
    return { value: triggers, label: 'Guardrails', format: 'number', color: triggers > 0 ? 'amber' : 'green', link: '/ai-operations' };
  },
});

/**
 * Parallel Executions Widget
 */
export const parallelExecutionsWidget = createMetricWidget({
  id: 'parallel-executions',
  name: 'Parallel Executions',
  description: 'Concurrent agent runs',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.activeRuns || 0,
    label: 'Parallel',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Retry Count Widget
 */
export const retryCountWidget = createMetricWidget({
  id: 'retry-count',
  name: 'Retry Count',
  description: 'Failed attempts retried',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const retries = runs.reduce((sum: number, r: AgentRun) => sum + (r.retries || 0), 0);
    return { value: retries, label: 'Retries', format: 'number', color: 'amber', link: '/ai-operations' };
  },
});

/**
 * Timeout Rate Widget
 */
export const timeoutRateWidget = createMetricWidget({
  id: 'timeout-rate',
  name: 'Timeout Rate',
  description: 'Execution timeouts',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const timeouts = runs.filter((r: AgentRun) => r.status === 'timeout').length;
    const rate = runs.length > 0 ? (timeouts / runs.length) * 100 : 0;
    return { value: rate, label: 'Timeout Rate', format: 'percentage', color: rate > 5 ? 'red' : 'green', link: '/ai-operations' };
  },
});

/**
 * Tool Errors Widget
 */
export const toolErrorsWidget = createMetricWidget({
  id: 'tool-errors',
  name: 'Tool Errors',
  description: 'Tool invocation failures',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const tools = sourceData?.tools || [];
    const errors = tools.reduce((sum: number, t: AgentTool) => sum + (t.error_count || 0), 0);
    return { value: errors, label: 'Tool Errors', format: 'number', color: errors > 0 ? 'red' : 'green', link: '/ai-operations' };
  },
});

/**
 * Context Window Usage Widget
 */
export const contextWindowUsageWidget = createMetricWidget({
  id: 'context-window-usage',
  name: 'Context Window',
  description: 'Context utilization %',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.apmMetrics?.context_usage || 0,
    label: 'Context Usage',
    format: 'percentage',
    color: 'cyan',
    link: '/ai-operations',
  }),
});

/**
 * Cost Per Run Widget
 */
export const costPerRunWidget = createMetricWidget({
  id: 'cost-per-run',
  name: 'Cost per Run',
  description: 'Average run cost',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const totalCost = runs.reduce((sum: number, r: AgentRun) => sum + (r.cost || 0), 0);
    const avg = runs.length > 0 ? totalCost / runs.length : 0;
    return { value: avg, label: 'Cost/Run', format: 'currency', color: 'green', link: '/ai-operations' };
  },
});

/**
 * Decision Points Widget
 */
export const decisionPointsWidget = createMetricWidget({
  id: 'decision-points',
  name: 'Decision Points',
  description: 'Agent decisions made',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const decisions = runs.reduce((sum: number, r: AgentRun) => sum + (r.decisions || r.steps || 0), 0);
    return { value: decisions, label: 'Decisions', format: 'number', color: 'blue', link: '/ai-operations' };
  },
});

/**
 * Human Interventions Widget
 */
export const humanInterventionsWidget = createMetricWidget({
  id: 'human-interventions',
  name: 'Human Interventions',
  description: 'Manual overrides',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const interventions = runs.reduce((sum: number, r: AgentRun) => sum + (r.human_interventions || 0), 0);
    return { value: interventions, label: 'Interventions', format: 'number', color: 'amber', link: '/ai-operations' };
  },
});

/**
 * Active Agents Count Widget
 */
export const activeAgentsCountWidget = createMetricWidget({
  id: 'active-agents-count',
  name: 'Active Agents',
  description: 'Currently running agents',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 30000,
  getData: (sourceData) => ({
    value: sourceData?.activeRuns || 0,
    label: 'Active Agents',
    format: 'number',
    color: 'green',
    link: '/ai-operations',
  }),
});

/**
 * Memory Usage Widget
 */
export const memoryUsageWidget = createMetricWidget({
  id: 'memory-usage',
  name: 'Memory Usage',
  description: 'Agent memory consumption',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const usage = sourceData?.heapUsage || 0;
    let memoryColor: string;
    if (usage > 80) {
      memoryColor = 'red';
    } else if (usage > 60) {
      memoryColor = 'amber';
    } else {
      memoryColor = 'green';
    }
    return { value: usage, label: 'Memory', format: 'percentage', color: memoryColor, link: '/ai-operations' };
  },
});

/**
 * Conversation Context Depth Widget
 */
export const conversationContextDepthWidget = createMetricWidget({
  id: 'conversation-context-depth',
  name: 'Context Depth',
  description: 'Conversation history length',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.apmMetrics?.context_depth || 0,
    label: 'Depth',
    format: 'number',
    color: 'purple',
    link: '/ai-operations',
  }),
});

/**
 * Memory Persistence Rate Widget
 */
export const memoryPersistenceRateWidget = createMetricWidget({
  id: 'memory-persistence-rate',
  name: 'Memory Persistence',
  description: 'Retained context %',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.apmMetrics?.memory_persistence || 0,
    label: 'Persistence',
    format: 'percentage',
    color: 'cyan',
    link: '/ai-operations',
  }),
});

/**
 * Incident Response Time Widget
 */
export const incidentResponseTimeWidget = createMetricWidget({
  id: 'incident-response-time',
  name: 'Response Time',
  description: 'Time to resolve',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.AGENTOPS,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const incidents = sourceData?.incidents || [];
    const resolved = incidents.filter((i: AgentIncident) => i.resolved_at);
    const avgTime = resolved.length > 0
      ? resolved.reduce((sum: number, i: AgentIncident) => sum + (new Date(i.resolved_at || '').getTime() - new Date(i.created_at || '').getTime()), 0) / resolved.length
      : 0;
    return { value: Math.round(avgTime / 60000), label: 'Response Time', format: 'duration', color: 'cyan', link: '/ai-operations' };
  },
});

/**
 * Runs By Outcome Chart Widget
 */
export const runsByOutcomeWidget = createChartWidget({
  id: 'runs-by-outcome',
  name: 'Runs by Outcome',
  description: 'Success/Failed/Timeout breakdown',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || [];
    const outcomes: Record<string, number> = { completed: 0, failed: 0, timeout: 0, running: 0 };
    runs.forEach((r: AgentRun) => { outcomes[r.status] = (outcomes[r.status] || 0) + 1; });
    const data = Object.entries(outcomes).map(([name, value]) => ({ name, value }));
    return { chartType: 'pie', data, dataKey: 'value', nameKey: 'name', colors: [GREEN, RED, AMBER, BLUE], link: '/agentops/runs' };
  },
});

/**
 * Incident Severity Chart Widget
 */
export const incidentSeverityWidget = createChartWidget({
  id: 'incident-severity',
  name: 'Incident Severity',
  description: 'Incidents by severity',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const incidents = sourceData?.incidents || [];
    const severities: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    incidents.forEach((i: AgentIncident) => {
      const key = i.severity?.toLowerCase() || 'low';
      severities[key] = (severities[key] || 0) + 1;
    });
    const data = Object.entries(severities).map(([name, value]) => ({ name, value }));
    return { chartType: 'horizontalBar', data, xKey: 'name', yKey: 'value', color: RED, link: '/agentops/safety' };
  },
});

/**
 * Run Trends Chart Widget
 */
interface TimelineEntry { date: string; count?: number; value?: number }
export const runTrendsWidget = createChartWidget({
  id: 'run-trends-chart',
  name: 'Run Trends',
  description: 'Agent run patterns',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const timeline = sourceData?.toolTimeline || [];
    const data = timeline.slice(-14).map((d: TimelineEntry) => ({
      period: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: d.count || d.value || 0,
    }));
    return { chartType: 'line', data: data.length > 0 ? data : [{ period: 'No data', value: 0 }], xKey: 'period', yKey: 'value', color: BLUE, link: '/agentops/runs' };
  },
});

/**
 * Tool Success Rate Chart Widget
 */
export const toolSuccessRateWidget = createChartWidget({
  id: 'tool-success-rate',
  name: 'Tool Success Rate',
  description: 'Success by tool type',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.AGENTOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.AGENTOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const tools = sourceData?.tools || [];
    const data = tools.slice(0, 8).map((t: AgentTool) => ({
      name: t.tool_name || t.name || 'Unknown',
      value: t.success_rate || 100,
    }));
    return { chartType: 'bar', data: data.length > 0 ? data : [{ name: 'No data', value: 0 }], xKey: 'name', yKey: 'value', color: GREEN, link: '/agentops/tools' };
  },
});

/**
 * Export all AgentOps widgets as an array
 */
export const AGENTOPS_WIDGETS = [
  agentRunsCountWidget,
  activeRunsWidget,
  successRateWidget,
  totalToolsWidget,
  safetyViolationsWidget,
  safetyScoreWidget,
  healthScoreWidget,
  heapUsageWidget,
  cpuUsageWidget,
  toolUsageWidget,
  runsTimelineWidget,
  toolSuccessWidget,
  incidentsWidget,
  topAgentsWidget,
  // Additional widgets (Phase 10)
  avgRunDurationWidget,
  toolCallsCountWidget,
  activeIncidentsWidget,
  agentTypesWidget,
  totalStepsWidget,
  avgStepsPerRunWidget,
  memoryStoreSizeWidget,
  guardrailTriggersWidget,
  parallelExecutionsWidget,
  retryCountWidget,
  timeoutRateWidget,
  toolErrorsWidget,
  contextWindowUsageWidget,
  costPerRunWidget,
  decisionPointsWidget,
  humanInterventionsWidget,
  activeAgentsCountWidget,
  memoryUsageWidget,
  conversationContextDepthWidget,
  memoryPersistenceRateWidget,
  incidentResponseTimeWidget,
  runsByOutcomeWidget,
  incidentSeverityWidget,
  runTrendsWidget,
  toolSuccessRateWidget,
];

/**
 * Export widgets as a map by ID for quick lookup
 */
export const AGENTOPS_WIDGETS_MAP = AGENTOPS_WIDGETS.reduce((acc: Record<string, WidgetTemplate>, widget) => {
  acc[widget.id] = widget;
  return acc;
}, {} as Record<string, WidgetTemplate>);

export default AGENTOPS_WIDGETS;
